import { useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { useSelector } from "react-redux";
import Config from "../Config";
import { toast } from "react-toastify";

const useOrderActions = () => {
  const queryClient = useQueryClient();
  const user = useSelector((state) => state.UserReducer.user);

  const apiCall = async ({ method = "post", endpoint, payload }) => {
    if (!user?.token) {
      console.error(
        "Order API call failed: No user token found in Redux state.",
        user
      );
      throw new Error("Authentication token not found.");
    }

    const config = {
      method,
      url: `${Config.apiUrl}/${endpoint}`,
      headers: {
        authorization: user.token,
        id: user.userId || user.id,
      },
      data: payload,
    };
    const { data } = await axios(config);
    return data;
  };

  const placeOrder = useMutation(
    (orderData) => apiCall({ endpoint: "orders", payload: orderData }),
    {
      onSuccess: (data) => {
        // Refresh cart data since order was placed successfully
        queryClient.invalidateQueries(["cart", user?.token]);
        toast.success("Order placed successfully!");
        return data;
      },
      onError: (error) => {
        console.error("Order placement failed:", error);
        toast.error("Failed to place order. Please try again.");
        throw error;
      },
    }
  );

  const createPaymentLink = useMutation(
    (paymentData) =>
      apiCall({
        endpoint: "payment/create-payment-link",
        payload: paymentData,
      }),
    {
      onSuccess: (data) => {
        console.log("Payment link generated successfully");
        return data;
      },
      onError: (error) => {
        console.error("Payment link creation failed:", error);
        toast.error(
          "Order created, but payment initiation failed. Please check 'My Orders'."
        );
        throw error;
      },
    }
  );

  const cancelOrder = useMutation(
    (orderData) =>
      apiCall({
        method: "put",
        endpoint: `orders/${
          orderData.orderId
        }/cancel?reason=${encodeURIComponent(orderData.reason)}`,
        payload: null,
      }),
    {
      onSuccess: (data) => {
        // Refresh orders list after cancellation
        queryClient.invalidateQueries(["orders", user?.token]);
        toast.success("Order cancelled successfully!");
        return data;
      },
      onError: (error) => {
        console.error("Order cancellation failed:", error);
        toast.error(error.response?.data?.message || "Failed to cancel order.");
        throw error;
      },
    }
  );

  const returnOrder = useMutation(
    (returnData) => {
      const formData = new FormData();

      formData.append("order_id", returnData.orderId);
      formData.append("product_id", returnData.productId);
      formData.append("reason", returnData.reason);
      formData.append("description", returnData.description);

      if (returnData.images && Array.isArray(returnData.images)) {
        returnData.images.forEach((file) => {
          formData.append("images", file);
        });
      }

      return apiCall({
        method: "post",
        endpoint: "returns",
        payload: formData,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["orders", user?.token]); // Refresh orders list after return
        queryClient.invalidateQueries(["returns", user?.token]); // Refresh returns list
        toast.success("Return request submitted successfully!");
      },
      onError: (error) => {
        console.error("Return request failed:", error);
        toast.error(
          error.response?.data?.message || "Failed to submit return request."
        );
      },
    }
  );
  // Function to get shipping address ID from user data
  const getShippingAddressId = () => {
    // Check if user has a default address ID stored in their profile
    if (user?.default_address_id) {
      return user.default_address_id;
    }

    // If no address ID is available, backend will use user's default address
    return null;
  };

  return {
    placeOrder,
    createPaymentLink,
    getShippingAddressId,
    isPlacingOrder: placeOrder.isLoading,
    isCreatingPaymentLink: createPaymentLink.isLoading,
    cancelOrder,
    isCancellingOrder: cancelOrder.isLoading,
    returnOrder,
    isReturningOrder: returnOrder.isLoading,
  };
};

export default useOrderActions;
